import React, { useState, useMemo, useEffect } from 'react'
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAutoReplies } from '@/hooks/useAutoReplies'
import { useReplyMetrics } from '@/hooks/useReplyMetrics'
import { useLinkedInReplies } from '@/hooks/useLinkedInReplies'
import { useLinkedInReplyMetrics } from '@/hooks/useLinkedInReplyMetrics'
import { RefreshCw, Eye, Mail, User, Building, Clock, Calendar, MessageSquare, ExternalLink, Bot, Settings, Upload, ThumbsUp, ThumbsDown, Search, Filter, X, Edit, Database, Tag, Loader2, ChevronDown, ChevronRight, Star, Archive, Reply, RotateCcw, UserCheck, Send, XCircle, CheckCircle2, Download, Bell } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'
import { LLMSettingsDialog, type LLMSettings } from '@/components/client-tam/LLMSettingsDialog'

import { TablePagination } from '@/components/ui/table-pagination'
import { DateRangeFilter } from '@/components/client-reporting/DateRangeFilter'
import { SlackChannelButtons } from '@/components/slack/SlackChannelButtons'
// Removed enrichment-related imports - no longer needed
import { RepliesTable } from '@/components/ai-reply-tagger/RepliesTable'
import { useBulkOperationProgress } from '@/hooks/useBulkOperationProgress'
import { BulkOperationProgress } from '@/components/mailbox-management/BulkOperationProgress'
import { NotificationSettingsTab } from '@/components/ai-reply-tagger/NotificationSettingsTab'
import { CrossChannelOrchestratorTab } from '@/components/ai-reply-tagger/CrossChannelOrchestratorTab'
import { useClientsList } from '@/hooks/useClients'
import { EmailReplyDetailsDialog } from '@/components/ai-reply-tagger/EmailReplyDetailsDialog'
import { LinkedInReplyDetailsDialog } from '@/components/ai-reply-tagger/LinkedInReplyDetailsDialog'
import { useDashboard } from '@/contexts/DashboardContext'

export default function AIReplyTagger() {
  // Get global client filter from DashboardContext (top filter)
  const { state } = useDashboard()
  const globalClientFilter = state.selectedClient
  
  // Get clients list early - needed for UUID to numeric client_id mapping
  const { data: allClients = [] } = useClientsList()
  
  // Map UUID client.id to numeric client_id for filtering
  // The global filter uses UUID (client.id), but LinkedIn replies use numeric client_id
  const [uuidToNumericClientIdMap, setUuidToNumericClientIdMap] = useState<Map<string, string>>(new Map())
  
  useEffect(() => {
    // Build mapping from UUID to numeric client_id
    const mapping = new Map<string, string>()
    allClients.forEach(client => {
      if (client.id && client.client_id) {
        mapping.set(client.id, client.client_id.toString())
      }
    })
    console.log('🔍 UUID to Numeric Client ID Mapping:', {
      totalClients: allClients.length,
      mappingSize: mapping.size,
      sampleMappings: Array.from(mapping.entries()).slice(0, 5)
    })
    setUuidToNumericClientIdMap(mapping)
  }, [allClients])

  // Date range and client filter state must be declared before useAutoReplies which depends on them
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  })
  const [clientFilter, setClientFilter] = useState<string>('all')

  const { replies, count, isLoading, error, refetch: refetchReplies, isFetching } = useAutoReplies({ dateRange, clientId: clientFilter !== 'all' ? clientFilter : undefined }) // Pass date range and client filter to fetch correct data from DB
  const { totalReplies, todayReplies, monthToDateReplies, positiveReplies, autoReplies, refetch: refetchMetrics } = useReplyMetrics()
  const { replies: linkedInReplies, count: linkedInCount, isLoading: isLoadingLinkedIn, error: linkedInError, refetch: refetchLinkedInReplies, isFetching: isFetchingLinkedIn } = useLinkedInReplies()
  const { totalReplies: linkedInTotalReplies, todayReplies: linkedInTodayReplies, monthToDateReplies: linkedInMonthToDateReplies, positiveReplies: linkedInPositiveReplies, autoReplies: linkedInAutoReplies, refetch: refetchLinkedInMetrics } = useLinkedInReplyMetrics()
  const [activeTab, setActiveTab] = useState<'replies' | 'linkedin' | 'notifications'>('replies')
  const [selectedReply, setSelectedReply] = useState<any>(null)
  const [selectedReplySource, setSelectedReplySource] = useState<'email' | 'linkedin' | null>(null)
  const [categorizingIds, setCategorizingIds] = useState<Set<string>>(new Set())
  // Removed updatingIds state - no longer needed
  const [fetchingReplyCountIds, setFetchingReplyCountIds] = useState<Set<string>>(new Set())
  const [fetchingReplyCount, setFetchingReplyCount] = useState(false)
  const [trackingSDRIds, setTrackingSDRIds] = useState<Set<string>>(new Set())
  const [ignoringIds, setIgnoringIds] = useState<Set<string>>(new Set())
  const [feedbackText, setFeedbackText] = useState('')
  const [submittingFeedback, setSubmittingFeedback] = useState(false)
  
  // TEMPORARY: Bulk ignore state for Cruva one-time fix
  const [bulkIgnoring, setBulkIgnoring] = useState(false)
  const [bulkIgnoreProgress, setBulkIgnoreProgress] = useState({ current: 0, total: 0, succeeded: 0, failed: 0 })
  
  // REMOVED: Bulk fetch state (archived - no longer needed)
  
  // Gmail-style features state
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set(['sent', 'reply']))
  const [starredMessages, setStarredMessages] = useState<Set<string>>(new Set())
  const [archivedMessages, setArchivedMessages] = useState<Set<string>>(new Set())
  const [threadSearch, setThreadSearch] = useState('')
  const [feedbackStatus, setFeedbackStatus] = useState<Record<string, { isPositive: boolean; message: string } | null>>({})

  // Gmail-style helper functions
  const toggleMessageExpansion = (messageId: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const toggleStar = (messageId: string) => {
    const newStarred = new Set(starredMessages);
    if (newStarred.has(messageId)) {
      newStarred.delete(messageId);
    } else {
      newStarred.add(messageId);
    }
    setStarredMessages(newStarred);
  };

  const toggleArchive = (messageId: string) => {
    const newArchived = new Set(archivedMessages);
    if (newArchived.has(messageId)) {
      newArchived.delete(messageId);
    } else {
      newArchived.add(messageId);
    }
    setArchivedMessages(newArchived);
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return format(date, 'MMM d');
  };

  const formatTimeToRespond = (seconds: number) => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };
  
  // Human-in-the-loop feedback state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [selectedReplyForFeedback, setSelectedReplyForFeedback] = useState<any>(null)
  const [correctionCategory, setCorrectionCategory] = useState('')
  const [correctionReason, setCorrectionReason] = useState('')
  const [submittingCorrection, setSubmittingCorrection] = useState(false)
  
  const [llmSettings, setLLMSettings] = useState<LLMSettings>({
    model: 'sonar',
    systemMessage: '',
    temperature: 0.1,
    topP: 1,
    topK: 50,
    maxTokens: 500,
    stopSequences: [],
    presencePenalty: 0,
    frequencyPenalty: 0,
    seed: undefined,
    streaming: false,
    jsonMode: false
  })
  // Toast is now imported directly from useToastHistory

  // Enrichment hooks
  // Removed enrichment-related hooks - no longer needed

  // Channel test sending state
  const [sendingToChannel, setSendingToChannel] = useState<Set<string>>(new Set())
  const [sendingTestNotificationId, setSendingTestNotificationId] = useState<string | null>(null)
  
  // Bulk operation progress hook
  const {
    operationId,
    isProgressOpen,
    isLoading: isStartingOperation,
    startOperation,
    updateOperation,
    closeProgress,
    onOperationComplete
  } = useBulkOperationProgress()
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingReply, setEditingReply] = useState<any>(null)
  const [editedFields, setEditedFields] = useState({
    response_category: '',
    to_email: '',
    campaign_name: '',
    subject: '',
    reply_body: ''
  })

  // Pagination and filtering states (Email Replies)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(15)
  const [searchTerm, setSearchTerm] = useState('')
  // clientFilter is declared earlier (before useAutoReplies) since it's used in the DB query
  const [categorizationFilter, setCategorizationFilter] = useState<string>('all')
  const [replyTypeFilter, setReplyTypeFilter] = useState<string>('all') // 'all', 'positive', 'neutral', 'negative', 'ooo', 'auto-reply'
  const [replyCountFilter, setReplyCountFilter] = useState<string>('all')
  const [sdrStatusFilter, setSdrStatusFilter] = useState<string>('all') // 'all', 'responded', 'waiting'

  // Pagination and filtering states (LinkedIn Replies)
  const [linkedInCurrentPage, setLinkedInCurrentPage] = useState(1)
  const [linkedInItemsPerPage, setLinkedInItemsPerPage] = useState(15)
  const [linkedInSearchTerm, setLinkedInSearchTerm] = useState('')
  const [linkedInClientFilter, setLinkedInClientFilter] = useState<string>('all')
  const [linkedInReplyTypeFilter, setLinkedInReplyTypeFilter] = useState<string>('all')
  const [linkedInDateRange, setLinkedInDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  })
  
  // Sync LinkedIn client filter with global filter (top filter)
  // Convert global filter (UUID) to numeric client_id for LinkedIn filtering
  // If global filter is set and local filter is 'all', use global filter (converted to numeric)
  // If local filter is explicitly set, use local filter (allows override)
  const effectiveLinkedInClientFilter = useMemo(() => {
    // If global filter is set and local filter is 'all', convert UUID to numeric client_id
    if (globalClientFilter && globalClientFilter !== 'all' && linkedInClientFilter === 'all') {
      // Check if global filter is already numeric (backwards compatibility)
      if (/^\d+$/.test(globalClientFilter)) {
        console.log('🔍 LinkedIn Filter: Global filter is numeric:', globalClientFilter)
        return globalClientFilter
      }
      // Convert UUID to numeric client_id
      const numericClientId = uuidToNumericClientIdMap.get(globalClientFilter)
      console.log('🔍 LinkedIn Filter: Converting UUID to numeric:', {
        globalClientFilter,
        numericClientId,
        mappingSize: uuidToNumericClientIdMap.size,
        mappingEntries: Array.from(uuidToNumericClientIdMap.entries()).slice(0, 3)
      })
      return numericClientId || 'all' // Fallback to 'all' if mapping not found
    }
    // Otherwise use local filter (allows user to override or set to 'all')
    console.log('🔍 LinkedIn Filter: Using local filter:', linkedInClientFilter)
    return linkedInClientFilter
  }, [globalClientFilter, linkedInClientFilter, uuidToNumericClientIdMap])
  
  // Sync local filter when global filter changes (if local filter is 'all')
  useEffect(() => {
    if (globalClientFilter && globalClientFilter !== 'all' && linkedInClientFilter === 'all') {
      // Global filter changed and local is 'all', sync it (convert UUID to numeric if needed)
      if (/^\d+$/.test(globalClientFilter)) {
        // Already numeric
        setLinkedInClientFilter(globalClientFilter)
      } else {
        // Convert UUID to numeric
        const numericClientId = uuidToNumericClientIdMap.get(globalClientFilter)
        if (numericClientId) {
          setLinkedInClientFilter(numericClientId)
        }
      }
    }
  }, [globalClientFilter, uuidToNumericClientIdMap])
  

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch {
      return 'Invalid Date'
    }
  }

  const getEventTypeColor = (eventType: string) => {
    switch (eventType?.toLowerCase()) {
      case 'reply_received':
        return 'bg-green-100 text-green-800'
      case 'email_sent':
        return 'bg-blue-100 text-blue-800'
      case 'email_opened':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getCampaignStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      case 'stopped':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const fetchReplyCount = async (reply: any, retryCount = 0) => {
    if (!reply.campaign_id || !reply.sl_email_lead_id || !reply.secret_key) {
      console.log('Missing required data for fetching reply count');
      return;
    }

    setFetchingReplyCountIds(prev => new Set(prev.add(reply.id)));
    
    try {
      console.log(`Fetching reply count for campaign ${reply.campaign_id}, lead ${reply.sl_email_lead_id}`);
      
      const smartleadUrl = `https://server.smartlead.ai/api/v1/campaigns/${reply.campaign_id}/leads/${reply.sl_email_lead_id}/message-history?api_key=${reply.secret_key}_80kb8ml`;
      
      const response = await fetch(smartleadUrl);
      
      if (response.ok) {
        const messageHistory = await response.json();
        console.log('Message history response:', messageHistory);
        
        // Count REPLY type messages in history
        let replyCount = 1; // Default
        if (messageHistory.history && Array.isArray(messageHistory.history)) {
          replyCount = messageHistory.history.filter(msg => msg.type === 'REPLY').length;
        }
        
        // PHASE 3 DAY 6: Update reply count in email_events (migrated table)
        const { error } = await supabase
          .from('obsync_dynamic_reports')
          .update({ reply_count: replyCount })
          .eq('id', reply.id);
        
        if (error) {
          console.error('Error updating reply count:', error);
        } else {
          console.log(`Updated reply count to ${replyCount} for reply ${reply.id}`);
          refetchReplies(); // Refresh the data
        }
      } else {
        console.error('Failed to fetch message history:', response.status, response.statusText);
        
        // Retry logic with exponential backoff
        if (retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
          setTimeout(() => fetchReplyCount(reply, retryCount + 1), delay);
          return; // Don't remove from fetchingReplyCountIds yet
        } else {
          toast({
            title: "Failed to fetch reply count",
            description: `Failed after 3 attempts for ${reply.to_email}`,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error fetching reply count:', error);
      
      // Retry logic
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => fetchReplyCount(reply, retryCount + 1), delay);
        return;
      } else {
        toast({
          title: "Error fetching reply count",
          description: error instanceof Error ? error.message : "Failed to fetch reply count",
          variant: "destructive"
        });
      }
    } finally {
      if (retryCount === 0 || retryCount >= 3) { // Only remove on first attempt success or final failure
        setFetchingReplyCountIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(reply.id);
          return newSet;
        });
      }
    }
  };

  const handleTrackSDRResponse = async (replyId: string) => {
    // Find the reply
    const reply = replies.find(r => r.id === replyId)
    if (!reply) {
      toast({
        title: "Reply Not Found",
        description: "Could not find the reply to track",
        variant: "destructive"
      })
      return
    }

    // Only allow tracking for positive replies
    if (reply.reply_category_type !== 'positive') {
      toast({
        title: "Not a Positive Reply",
        description: "SDR tracking is only available for positive replies",
        variant: "destructive"
      })
      return
    }

    // Check if required fields are present
    if (!reply.campaign_id || !reply.sl_email_lead_id) {
      toast({
        title: "Missing Required Data",
        description: "Campaign ID or Lead ID is missing. Cannot track SDR response.",
        variant: "destructive"
      })
      return
    }

    setTrackingSDRIds(prev => new Set(prev.add(replyId)))
    
    try {
      console.log('🔍 Invoking SDR tracker for reply:', replyId)
      
      // Call the SDR tracker function for this specific reply
      const { data, error } = await supabase.functions.invoke('sdr-response-tracker', {
        body: { 
          replyId: replyId,
          campaignId: reply.campaign_id,
          leadId: reply.sl_email_lead_id
        }
      })

      console.log('📥 SDR tracker response:', { data, error })

      if (error) {
        console.error('❌ SDR tracker error:', error)
        // Check if error has context (Response object)
        if (error.context) {
          try {
            if (error.context instanceof Response) {
              const errorBody = await error.context.clone().json()
              console.error('❌ Parsed error response:', errorBody)
              throw new Error(errorBody.error || errorBody.message || error.message || 'Failed to invoke SDR tracker function')
            }
          } catch (parseError) {
            console.error('❌ Could not parse error response:', parseError)
          }
        }
        throw new Error(error.message || 'Failed to invoke SDR tracker function')
      }

      if (!data) {
        throw new Error('No response data received from SDR tracker function')
      }

      if (data?.success) {
        toast({
          title: "SDR Tracking Complete",
          description: data.message || "SDR response status has been updated",
        })
        refetchReplies() // Refresh the data to show updated SDR tracking status
      } else {
        throw new Error(data?.error || data?.message || 'SDR tracking failed')
      }
    } catch (error: any) {
      console.error('❌ SDR tracking error:', error)
      const errorMessage = error?.message || error?.error || "Failed to track SDR response. Please try again."
      toast({
        title: "SDR Tracking Failed",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setTrackingSDRIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(replyId)
        return newSet
      })
    }
  }

  const handleIgnoreReply = async (replyId: string) => {
    // Find the reply
    const reply = replies.find(r => r.id === replyId)
    if (!reply) {
      toast({
        title: "Reply Not Found",
        description: "Could not find the reply to ignore",
        variant: "destructive"
      })
      return
    }

    // Validate that this is an auto-reply type category (SAFETY CHECK)
    const allowedCategories = ['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'];
    if (!allowedCategories.includes(reply.response_category || '')) {
      toast({
        title: "Cannot Ignore This Reply",
        description: `Only auto-reply types can be ignored in Smartlead. This reply is categorized as "${reply.response_category}".`,
        variant: "destructive"
      })
      return
    }

    // Check if stats_id exists
    if (!reply.stats_id) {
      toast({
        title: "Missing Stats ID",
        description: "This reply doesn't have a Smartlead stats_id. Cannot ignore.",
        variant: "destructive"
      })
      return
    }

    // Check if already ignored
    if (reply.smartlead_ignore_status === 'success') {
      toast({
        title: "Already Ignored",
        description: "This reply is already ignored in Smartlead stats",
      })
      return
    }

    setIgnoringIds(prev => new Set(prev.add(replyId)))
    
    try {
      console.log('🔕 Invoking ignore-reply for:', replyId, 'stats_id:', reply.stats_id)
      
      // Call the ignore-reply function
      const { data, error } = await supabase.functions.invoke('ignore-reply-smartlead', {
        body: { 
          replyId: replyId,
          statsId: reply.stats_id,
          retryCount: 0
        }
      })

      console.log('📥 Ignore-reply response:', { data, error })

      if (error) {
        console.error('❌ Ignore-reply error:', error)
        throw new Error(error.message || 'Failed to invoke ignore-reply function')
      }

      if (!data) {
        throw new Error('No response data received from ignore-reply function')
      }

      if (data?.success) {
      toast({
        title: "Reply Ignored Successfully",
        description: "This reply has been excluded from Smartlead stats",
      })
      refetchReplies() // Refresh the data to show updated ignore status
      } else {
        throw new Error(data?.error || 'Failed to ignore reply')
      }
    } catch (error: any) {
      console.error('❌ Failed to ignore reply:', error)
      toast({
        title: "Ignore Failed",
        description: error.message || "Failed to ignore reply in Smartlead. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIgnoringIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(replyId)
        return newSet
      })
    }
  }

  // TEMPORARY: Bulk ignore function for Cruva one-time fix
  // TODO: REMOVE THIS FUNCTION AFTER BULK IGNORE IS COMPLETE
  const handleBulkIgnoreCruvaAutoReplies = async () => {
    const CRUVA_CLIENT_ID = 167887
    const autoReplyCategories = ['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'];
    
    // Find all Cruva auto-replies that need to be ignored
    const cruvaRepliesToIgnore = filteredReplies.filter(reply => 
      reply.client_id === CRUVA_CLIENT_ID &&
      autoReplyCategories.includes(reply.response_category || '') &&
      reply.reply_count === 1 &&
      reply.stats_id &&
      reply.smartlead_ignore_status !== 'success'
    );

    if (cruvaRepliesToIgnore.length === 0) {
      toast({
        title: "No Replies to Ignore",
        description: "All eligible Cruva auto-replies have already been ignored",
      })
      return
    }

    const confirmed = window.confirm(
      `Found ${cruvaRepliesToIgnore.length} Cruva auto-replies to ignore in Smartlead.\n\n` +
      `This will process them in batches of 5 with 1 second delay between batches.\n\n` +
      `Continue?`
    )

    if (!confirmed) return

    setBulkIgnoring(true)
    setBulkIgnoreProgress({ current: 0, total: cruvaRepliesToIgnore.length, succeeded: 0, failed: 0 })

    let succeeded = 0
    let failed = 0
    const errors: string[] = []

    // Process in batches of 5
    const BATCH_SIZE = 5
    for (let i = 0; i < cruvaRepliesToIgnore.length; i += BATCH_SIZE) {
      const batch = cruvaRepliesToIgnore.slice(i, i + BATCH_SIZE)
      
      // Process batch in parallel
      const batchPromises = batch.map(async (reply) => {
        try {
          const { data, error } = await supabase.functions.invoke('ignore-reply-smartlead', {
            body: { 
              replyId: reply.id,
              statsId: reply.stats_id,
              retryCount: 0
            }
          })

          if (error || !data?.success) {
            throw new Error(data?.error || error?.message || 'Unknown error')
          }

          succeeded++
          return { success: true, email: reply.from_email }
        } catch (error) {
          failed++
          const errorMsg = `${reply.from_email}: ${error instanceof Error ? error.message : String(error)}`
          errors.push(errorMsg)
          return { success: false, email: reply.from_email, error: errorMsg }
        } finally {
          setBulkIgnoreProgress(prev => ({ ...prev, current: prev.current + 1, succeeded, failed }))
        }
      })

      await Promise.all(batchPromises)

      // Delay between batches (1 second)
      if (i + BATCH_SIZE < cruvaRepliesToIgnore.length) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    }

    setBulkIgnoring(false)
    refetchReplies()
    refetchMetrics()

    toast({
      title: "Bulk Ignore Complete",
      description: `Successfully ignored ${succeeded} Cruva replies. ${failed} failed.${errors.length > 0 && errors.length <= 5 ? '\n\nErrors:\n' + errors.join('\n') : errors.length > 5 ? '\n\nFirst 5 errors:\n' + errors.slice(0, 5).join('\n') : ''}`,
      variant: succeeded > 0 ? "default" : "destructive"
    })
  }
  // END TEMPORARY: Bulk ignore function

  const handleCategorize = async (replyId: string, forceSync: boolean = false) => {
    // Find the reply to check reply_count
    const reply = replies.find(r => r.id === replyId)
    if (!reply) {
      toast({
        title: "Reply Not Found",
        description: "Could not find the reply to categorize",
        variant: "destructive"
      })
      return
    }

    // Only allow categorization for replies with reply_count of 1 or 2
    if (reply.reply_count && reply.reply_count > 2) {
      toast({
        title: "Cannot Categorize",
        description: "Only replies with a reply count of 1 or 2 can be categorized",
        variant: "destructive"
      })
      return
    }

    setCategorizingIds(prev => new Set(prev.add(replyId)))
    
    try {
      // Step 1: Categorize the reply using AI
      const { data, error } = await supabase.functions.invoke('categorize-reply', {
        body: { 
          replyId,
          llmSettings
        }
      })

      if (error) throw error

      if (data.success) {
        // Handle skipped categorizations (no categorization object)
        if (data.skipped && !data.categorization) {
          toast({
            title: "Categorization Skipped",
            description: data.message || `Skipped: ${data.reason || 'unknown reason'}`,
            variant: "default"
          })
          // Refresh to show current state (might have been updated by another process)
          refetchReplies()
          refetchMetrics()
          return
        }

        // Ensure categorization exists before accessing its properties
        if (!data.categorization) {
          throw new Error("Categorization response missing categorization data")
        }

        // Step 2: Auto-sync to Smartlead for all clients with Smartlead data (except Yotpo), or when manually forced
        // Auto-sync if reply has Smartlead identifiers (campaign_id and sl_email_lead_id) AND client is NOT Yotpo (5559)
        // This works for all clients automatically (Loop, Cruva, StoryStream, and future clients) except Yotpo
        let smartleadSyncMessage = ""
        const hasSmartleadData = !!(reply.campaign_id && reply.sl_email_lead_id)
        const isYotpoClient = reply.client_id === 5559
        const shouldSync = forceSync || (hasSmartleadData && !isYotpoClient)  // Auto-sync for all clients with Smartlead data except Yotpo, or manual sync
        
        if (shouldSync && reply.campaign_id && reply.sl_email_lead_id && data.categorization?.confidence) {
          try {
            console.log('🔄 Syncing to Smartlead:', {
              campaignId: reply.campaign_id,
              leadId: reply.sl_email_lead_id,
              categoryId: data.categorization.confidence, // This is actually the category_id
              categoryName: data.categorization.category,
              clientId: reply.client_id,
              syncType: forceSync ? 'manual' : 'auto'
            })

            const { data: smartleadData, error: smartleadError } = await supabase.functions.invoke('update-smartlead-category', {
              body: {
                campaignId: reply.campaign_id,
                leadId: reply.sl_email_lead_id,
                categoryId: data.categorization.confidence // This is the response_category_id
              }
            })

            if (smartleadError) {
              console.error('❌ Smartlead sync failed:', smartleadError)
              smartleadSyncMessage = " (Smartlead sync failed - category updated locally only)"
            } else if (smartleadData?.success) {
              console.log('✅ Smartlead sync successful:', smartleadData)
              smartleadSyncMessage = " ✅ (Synced to Smartlead)"
            } else {
              console.warn('⚠️ Smartlead sync returned no success:', smartleadData)
              smartleadSyncMessage = " (Smartlead sync status unknown)"
            }
          } catch (smartleadError) {
            console.error('❌ Smartlead sync error:', smartleadError)
            smartleadSyncMessage = " (Smartlead sync failed - category updated locally only)"
          }
        } else if (!shouldSync) {
          smartleadSyncMessage = " (Use manual sync button to sync)"
        } else {
          smartleadSyncMessage = " (No Smartlead sync - missing campaign/lead data)"
        }

        toast({
          title: "Reply Categorized",
          description: `Categorized as: ${data.categorization.category}${smartleadSyncMessage}`,
        })
        // Refresh both replies and metrics to show the new categorization
        refetchReplies()
        refetchMetrics()
      } else {
        throw new Error(data.error || "Categorization failed")
      }
    } catch (error) {
      console.error('Categorization error:', error)
      toast({
        title: "Categorization Failed",
        description: error instanceof Error ? error.message : "Failed to categorize reply",
        variant: "destructive"
      })
    } finally {
      setCategorizingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(replyId)
        return newSet
      })
    }
  }

  // LinkedIn categorization handler (no Smartlead sync - email only)
  const handleCategorizeLinkedIn = async (replyId: string) => {
    // Find the LinkedIn reply
    const reply = linkedInReplies.find(r => r.id === replyId)
    if (!reply) {
      toast({
        title: "Reply Not Found",
        description: "Could not find the LinkedIn reply to categorize",
        variant: "destructive"
      })
      return
    }

    // Verify campaign_id exists (required for LinkedIn categorization)
    if (!reply.campaign_id) {
      toast({
        title: "Cannot Categorize",
        description: "LinkedIn reply must have campaign_id for categorization",
        variant: "destructive"
      })
      return
    }

    setCategorizingIds(prev => new Set(prev.add(replyId)))
    
    try {
      // Categorize the LinkedIn reply using AI
      const { data, error } = await supabase.functions.invoke('categorize-linkedin-reply', {
        body: { 
          replyId
        }
      })

      if (error) throw error

      if (data.success) {
        // Handle skipped categorizations
        if (data.skipped && !data.categorization) {
          toast({
            title: "Categorization Skipped",
            description: data.message || `Skipped: ${data.reason || 'unknown reason'}`,
            variant: "default"
          })
          // Refresh to show current state
          refetchLinkedInReplies()
          return
        }

        // Ensure categorization exists
        if (!data.categorization) {
          throw new Error("Categorization response missing categorization data")
        }

        toast({
          title: "LinkedIn Reply Categorized",
          description: `Categorized as: ${data.categorization.category}`,
        })
        // Refresh LinkedIn replies to show the new categorization
        refetchLinkedInReplies()
      } else {
        throw new Error(data.error || "Categorization failed")
      }
    } catch (error) {
      console.error('LinkedIn categorization error:', error)
      toast({
        title: "Categorization Failed",
        description: error instanceof Error ? error.message : "Failed to categorize LinkedIn reply",
        variant: "destructive"
      })
    } finally {
      setCategorizingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(replyId)
        return newSet
      })
    }
  }

  // Send real-time notification to client's actual Slack channels
  const handleSendTestNotification = async (replyId: string, notificationType: 'positive' | 'negative') => {
    setSendingTestNotificationId(replyId)
    
    try {
      // Find the reply to get its client_id
      const reply = replies.find(r => r.id === replyId)
      if (!reply) {
        throw new Error('Reply not found')
      }
      
      // ALWAYS use Test Client (99999) for manual test notifications
      const TEST_CLIENT_ID = '99999'
      console.log(`🧪 Sending test ${notificationType} notification for reply ${replyId} to Test Client channels`)
      
      // Call Edge Function with Test Client ID - will route to test channels
      const { data, error } = await supabase.functions.invoke('scheduled-slack-reports', {
        body: {
          reportType: notificationType === 'positive' ? 'positiveReplyNotification' : 'negativeReplyNotification',
          clientId: TEST_CLIENT_ID, // Always use Test Client for manual tests
          channelType: notificationType === 'positive' ? 'positive_replies' : 'neutral_negative_replies',
          dryRun: false,
          replyId: replyId,
          triggerSource: 'manual_test_button'
        }
      })

      if (error) throw error

      if (data?.success) {
        toast({
          title: "Test Notification Sent!",
          description: `${notificationType === 'positive' ? 'Positive' : 'Negative/Neutral'} notification sent to test channels`,
        })
      } else {
        throw new Error(data?.error || 'Unknown error')
      }
    } catch (error: any) {
      console.error('Test notification error:', error)
      toast({
        title: "Test Notification Failed",
        description: error.message || "Failed to send test notification",
        variant: "destructive"
      })
    } finally {
      setSendingTestNotificationId(null)
    }
  }

  // Add new function for manual Smartlead sync
  const handleManualSmartleadSync = async (replyId: string) => {
    const reply = replies.find(r => r.id === replyId)
    if (!reply) {
      toast({
        title: "Reply Not Found",
        variant: "destructive"
      })
      return
    }

    if (!reply.campaign_id || !reply.sl_email_lead_id) {
      toast({
        title: "Cannot Sync",
        description: "Missing campaign_id or lead_id for Smartlead sync",
        variant: "destructive"
      })
      return
    }

    if (!reply.response_category_id) {
      toast({
        title: "Cannot Sync",
        description: "Reply must be categorized first",
        variant: "destructive"
      })
      return
    }

    setCategorizingIds(prev => new Set(prev.add(replyId)))
    
    try {
      console.log('🔄 Manual Smartlead sync:', {
        campaignId: reply.campaign_id,
        leadId: reply.sl_email_lead_id,
        categoryId: reply.response_category_id,
        categoryName: reply.response_category
      })

      const { data, error } = await supabase.functions.invoke('update-smartlead-category', {
        body: {
          campaignId: reply.campaign_id,
          leadId: reply.sl_email_lead_id,
          categoryId: reply.response_category_id
        }
      })

      if (error) throw error

      if (data?.success) {
        console.log('✅ Manual sync successful:', data)
        toast({
          title: "Synced to Smartlead",
          description: `Category "${reply.response_category}" synced successfully`,
        })
      } else {
        throw new Error('Sync failed without error message')
      }
    } catch (error) {
      console.error('❌ Manual sync error:', error)
      toast({
        title: "Sync Failed",
        description: error instanceof Error ? error.message : "Failed to sync to Smartlead",
        variant: "destructive"
      })
    } finally {
      setCategorizingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(replyId)
        return newSet
      })
    }
  }

  // Removed handleUpdateCategory function - no longer needed

  // Handle opening in Smartlead - Fresh implementation using only app_url
  const handleViewInSmartlead = (reply: any) => {
    if (!reply.app_url) {
      toast({
        title: "No Smartlead URL available",
        description: "This reply doesn't have a Smartlead URL to open.",
        variant: "destructive"
      });
      return;
    }
    
    // Open the app_url directly - this should land in Smartlead's Master Inbox
    window.open(reply.app_url, '_blank', 'noopener,noreferrer');
  }

  // Handle incorrect labeling feedback
  const handleIncorrectLabeling = (reply: any) => {
    setSelectedReplyForFeedback(reply)
    setCorrectionCategory('')
    setCorrectionReason('')
    setFeedbackModalOpen(true)
  }

  // Submit feedback correction
  const handleSubmitFeedback = async () => {
    if (!selectedReplyForFeedback || !correctionCategory || !correctionReason) {
      toast({
        title: "Missing Information",
        description: "Please provide both the correct category and reason for the correction",
        variant: "destructive"
      })
      return
    }

    setSubmittingCorrection(true)
    
    try {
      // Insert feedback into database
      const { error } = await supabase
        .from('ai_categorization_feedback')
        .insert({
          reply_id: selectedReplyForFeedback.id,
          is_positive_feedback: false,
          feedback_message: `Category corrected from "${selectedReplyForFeedback.response_category}" to "${correctionCategory}". Reason: ${correctionReason}`
      })

      if (error) throw error

      // Update the activity with the corrected category
      const { error: updateError } = await supabase
        .from('activities')
        .update({
          sentiment: correctionCategory,
          category: correctionCategory,
          metadata: {
            ...selectedReplyForFeedback.metadata,
            response_category: correctionCategory,
            response_category_id: getCategoryId(correctionCategory),
            reasoning: `Human correction: ${correctionReason}`,
            corrected_at: new Date().toISOString(),
            corrected_by: 'user'
          }
        })
        .eq('id', selectedReplyForFeedback.id)

      if (updateError) throw updateError

        toast({
        title: "Feedback Submitted",
        description: "Thank you for the correction. This will help improve future categorizations.",
        variant: "default"
      })

      setFeedbackModalOpen(false)
      refetchReplies() // Refresh the data
    } catch (error) {
      console.error('Error submitting feedback:', error)
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit feedback",
        variant: "destructive"
      })
    } finally {
      setSubmittingCorrection(false)
    }
  }

  // Helper function to get category ID from category name
  const getCategoryId = (categoryName: string): number => {
    const categoryMap: Record<string, number> = {
      'Interested': 1,
      'Not Interested': 2,
      'Requesting More Info': 3,
      'Out Of Office': 6,
      'Auto-Reply': 20767,
      'Sender Originated Bounce': 9,
      'Campaign Mess Up': 25552,
      'Uncategorizable by AI': 8
    }
    return categoryMap[categoryName] || 8
  }

  // Fetch existing feedback for a reply
  const fetchFeedbackStatus = async (replyId: string) => {
    try {
      const { data, error } = await supabase
        .from('ai_categorization_feedback')
        .select('is_positive_feedback, feedback_message')
        .eq('reply_id', replyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error

      if (data) {
        setFeedbackStatus(prev => ({
          ...prev,
          [replyId]: {
            isPositive: data.is_positive_feedback,
            message: data.feedback_message || ''
          }
        }))
      }
    } catch (error) {
      console.error('Error fetching feedback status:', error)
    }
  }

  const handleFeedback = async (replyId: string, isPositive: boolean, feedbackMessage: string) => {
    if (!replyId) {
      toast({
        title: "Error",
        description: "Reply ID is missing",
        variant: "destructive"
      })
      return
    }

    setSubmittingFeedback(true)

    try {
      const { error } = await supabase
        .from('ai_categorization_feedback')
        .insert({
          reply_id: replyId,
          is_positive_feedback: isPositive,
          feedback_message: feedbackMessage,
          submitted_at: new Date().toISOString()
        })

      if (error) throw error

      // Update local state to show feedback has been provided
      setFeedbackStatus(prev => ({
        ...prev,
        [replyId]: { isPositive, message: feedbackMessage }
      }))

      toast({
        title: "Feedback Submitted",
        description: "Thank you for your feedback! This will help improve future categorizations. Note: This feedback is stored for analysis but doesn't automatically retrain the AI model.",
      })

      setFeedbackText('') // Clear the feedback text
    } catch (error) {
      console.error('Feedback submission error:', error)
      toast({
        title: "Feedback Failed",
        description: error instanceof Error ? error.message : "Failed to submit feedback",
        variant: "destructive"
      })
    } finally {
      setSubmittingFeedback(false)
    }
  }

  // REMOVED: Bulk fetch functions (archived - no longer needed)
  // Functions archived to supabase/functions/_archived/
  // - bulk-fetch-reply-counts
  // - bulk-fetch-missing-reply-counts
  // - fetch-missing-reply-counts
  // Individual fetch-reply-count remains active for manual use


  // REMOVED: August reply categorization (archived - now using automatic trigger)
  // Function archived to supabase/functions/_archived/categorize-august-replies

  // Manual fetch reply count function
  const handleManualFetchReplyCount = async (reply: any) => {
    if (!reply.campaign_id || !reply.sl_email_lead_id) {
      toast({
        title: "Missing Data",
        description: "Campaign ID and Lead ID are required to fetch message history",
        variant: "destructive"
      })
      return
    }

    setFetchingReplyCount(true)
    
    try {
      console.log(`Manual fetch for reply ${reply.id}: Campaign ${reply.campaign_id}, Lead ${reply.sl_email_lead_id}`)
      
      const { data, error } = await supabase.functions.invoke('fetch-reply-count', {
        body: {
          record_id: reply.id,
          campaign_id: reply.campaign_id.toString(),
          lead_id: reply.sl_email_lead_id.toString()
        }
      })

      if (error) throw error

      toast({
        title: "Success",
        description: `Message history fetched! Reply count: ${data.reply_count}`,
        variant: "default"
      })

      // Refresh the data to show updated reply count
      refetchReplies()
      
    } catch (error) {
      console.error('Error fetching reply count:', error)
      toast({
        title: "Fetch Failed",
        description: error instanceof Error ? error.message : "Failed to fetch message history",
        variant: "destructive"
      })
    } finally {
      setFetchingReplyCount(false)
    }
  }

  // Filtering and pagination logic
  const filteredReplies = useMemo(() => {
    let filtered = [...replies]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(reply => 
        (reply.to_email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (reply.to_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (reply.from_email?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(reply => reply.client_id?.toString() === clientFilter)
    }

    // Categorization filter
    if (categorizationFilter !== 'all') {
      if (categorizationFilter === 'categorized') {
        filtered = filtered.filter(reply => reply.response_category)
      } else if (categorizationFilter === 'not_categorized') {
        filtered = filtered.filter(reply => !reply.response_category)
      } else if (categorizationFilter === 'positive') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'positive')
      } else if (categorizationFilter === 'auto_replies_to_ignore') {
        // Show only auto-reply types that should be ignored
        const autoReplyCategories = ['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'];
        filtered = filtered.filter(reply => 
          autoReplyCategories.includes(reply.response_category || '') && reply.reply_count === 1
        )
      } else {
        // Filter by specific category name (for the 15 audit categories)
        filtered = filtered.filter(reply => reply.response_category === categorizationFilter)
      }
    }

    // Reply Type filter (based on reply_category_type)
    if (replyTypeFilter !== 'all') {
      if (replyTypeFilter === 'positive') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'positive')
      } else if (replyTypeFilter === 'neutral') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'neutral')
      } else if (replyTypeFilter === 'negative') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'negative')
      } else if (replyTypeFilter === 'ooo') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'ooo' || reply.response_category === 'Out Of Office')
      } else if (replyTypeFilter === 'auto-reply') {
        filtered = filtered.filter(reply => reply.reply_category_type === 'auto-reply' || reply.response_category === 'Auto-Reply')
      } else if (replyTypeFilter === 'uncategorized') {
        filtered = filtered.filter(reply => !reply.reply_category_type && !reply.response_category)
      }
    }

    // Reply count filter
    if (replyCountFilter !== 'all') {
      if (replyCountFilter === '1') {
        filtered = filtered.filter(reply => reply.reply_count === 1)
      } else if (replyCountFilter === '2') {
        filtered = filtered.filter(reply => reply.reply_count === 2)
      } else if (replyCountFilter === 'later') {
        filtered = filtered.filter(reply => reply.reply_count && reply.reply_count > 2)
      }
    }

    // Date range filter
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(reply => {
        const replyDate = new Date(reply.created_at)
        if (dateRange.from && replyDate < dateRange.from) return false
        if (dateRange.to && replyDate > dateRange.to) return false
        return true
      })
    }

    // SDR Status filter (only for positive replies)
    if (sdrStatusFilter !== 'all') {
      filtered = filtered.filter(reply => {
        // Only apply SDR status filter to positive replies
        if (reply.reply_category_type !== 'positive') {
          return false // Exclude non-positive replies when filtering by SDR status
        }
        
        const hasResponded = reply.average_time_to_respond !== null && reply.average_time_to_respond !== undefined
        const waitingForReply = reply.lead_without_a_reply === true
        const status = reply.sdr_response_tracking_status || 'not_tracked'
        
        if (sdrStatusFilter === 'responded') {
          return hasResponded
        } else if (sdrStatusFilter === 'waiting') {
          return waitingForReply || status === 'tracking'
        }
        
        return true
      })
    }

    return filtered
  }, [replies, searchTerm, clientFilter, categorizationFilter, replyTypeFilter, replyCountFilter, sdrStatusFilter, dateRange])

  // Pagination logic
  const totalPages = Math.ceil(filteredReplies.length / itemsPerPage)
  const paginatedReplies = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredReplies.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredReplies, currentPage, itemsPerPage])

  // Build comprehensive campaign_id -> client_id mapping for LinkedIn replies
  // This ensures client filtering works even when client_id is null in the reply data
  const [linkedInCampaignToClientMap, setLinkedInCampaignToClientMap] = useState<Map<string | number, string>>(new Map())
  
  useEffect(() => {
    const buildCampaignToClientMapping = async () => {
      try {
        // Get all LinkedIn variants with their heyreach_campaign_id
        const { data: variants, error: variantsError } = await supabase
          .from('ab_test_variants')
          .select('heyreach_campaign_id, segment_id')
          .eq('channel', 'linkedin')
          .not('heyreach_campaign_id', 'is', null)
        
        if (variantsError || !variants || variants.length === 0) {
          console.log('⚠️ No LinkedIn variants found for client mapping')
          return
        }
        
        const segmentIds = [...new Set(variants.map(v => v.segment_id).filter(Boolean))]
        
        // Get segments with campaign_id
        const { data: segments, error: segmentsError } = await supabase
          .from('ab_test_segments')
          .select('id, campaign_id')
          .in('id', segmentIds)
        
        if (segmentsError || !segments || segments.length === 0) {
          console.log('⚠️ No segments found for LinkedIn variants')
          return
        }
        
        const campaignIds = [...new Set(segments.map(s => s.campaign_id).filter(Boolean))]
        
        // Get campaigns with client_id
        const { data: campaigns, error: campaignsError } = await supabase
          .from('campaigns')
          .select('id, client_id')
          .in('id', campaignIds)
        
        if (campaignsError || !campaigns || campaigns.length === 0) {
          console.log('⚠️ No campaigns found for LinkedIn segments')
          return
        }
        
        // Build mapping: segment_id -> campaign_id -> client_id
        const segmentToCampaignMap = new Map(segments.map(s => [s.id, s.campaign_id]))
        const campaignToClientMap = new Map(campaigns.map(c => [c.id, c.client_id?.toString()]))
        
        // Build final mapping: heyreach_campaign_id -> client_id
        // Store both string and number versions of campaign_id as keys for flexible lookup
        const mapping = new Map<string | number, string>()
        variants.forEach(variant => {
          if (variant.heyreach_campaign_id) {
            const segmentId = variant.segment_id
            const campaignId = segmentToCampaignMap.get(segmentId)
            if (campaignId) {
              const clientId = campaignToClientMap.get(campaignId)
              if (clientId) {
                const heyreachId = variant.heyreach_campaign_id
                // Store with both string and number keys for flexible lookup
                mapping.set(heyreachId, clientId)
                mapping.set(String(heyreachId), clientId)
                if (typeof heyreachId === 'string' && !isNaN(Number(heyreachId))) {
                  mapping.set(Number(heyreachId), clientId)
                }
              }
            }
          }
        })
        
        console.log(`✅ Built LinkedIn campaign to client mapping: ${mapping.size} campaigns mapped`)
        setLinkedInCampaignToClientMap(mapping)
      } catch (error) {
        console.error('❌ Error building LinkedIn campaign to client mapping:', error)
      }
    }
    
    buildCampaignToClientMapping()
  }, []) // Run once on mount
  
  const uniqueClients = useMemo(() => {
    // Create a map of numeric client_id to client name
    const clientIdToNameMap = new Map<number, string>()
    allClients.forEach(client => {
      if (client.client_id) {
        clientIdToNameMap.set(client.client_id, client.name)
      }
    })
    
    // Return all active clients with numeric client_id, using client_id for filtering
    return allClients
      .filter(client => client.client_id != null) // Only include clients with numeric client_id
      .map(client => ({
        id: client.client_id!.toString(), // Use numeric client_id as string for filter value
        name: client.name
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [allClients])

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, clientFilter, categorizationFilter, replyTypeFilter, replyCountFilter, sdrStatusFilter, dateRange])

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('')
    setClientFilter('all')
    setCategorizationFilter('all')
    setReplyTypeFilter('all')
    setReplyCountFilter('all')
    setSdrStatusFilter('all')
    setDateRange({ from: undefined, to: undefined })
  }

  // Build campaign_id to client_id mapping for LinkedIn replies (fallback when client_id is null)
  // Combine mapping from replies data and comprehensive database mapping
  const campaignIdToClientIdMap = useMemo(() => {
    const map = new Map<string | number, string>(linkedInCampaignToClientMap)
    
    // Also extract from replies that have client_id (in case database mapping missed some)
    linkedInReplies.forEach(reply => {
      if (reply.campaign_id && reply.client_id) {
        map.set(reply.campaign_id, reply.client_id.toString())
      }
    })
    
    return map
  }, [linkedInReplies, linkedInCampaignToClientMap])
  
  // Build client name to client_id mapping for fallback filtering
  const clientNameToIdMap = useMemo(() => {
    const map = new Map<string, string>()
    allClients.forEach(client => {
      if (client.name && client.client_id) {
        map.set(client.name.toLowerCase(), client.client_id.toString())
      }
    })
    return map
  }, [allClients])
  
  // Get the selected client name from the filter (for fallback matching)
  const selectedClientName = useMemo(() => {
    if (effectiveLinkedInClientFilter === 'all') return null
    // Try to find client name by numeric client_id
    const client = allClients.find(c => c.client_id?.toString() === effectiveLinkedInClientFilter)
    return client?.name || null
  }, [effectiveLinkedInClientFilter, allClients])

  // Filtering logic for LinkedIn Replies with client filtering support
  const filteredLinkedInReplies = useMemo(() => {
    let filtered = [...linkedInReplies]

    // Search filter
    if (linkedInSearchTerm) {
      filtered = filtered.filter(reply => 
        (reply.to_email?.toLowerCase().includes(linkedInSearchTerm.toLowerCase())) ||
        (reply.to_name?.toLowerCase().includes(linkedInSearchTerm.toLowerCase())) ||
        (reply.from_email?.toLowerCase().includes(linkedInSearchTerm.toLowerCase())) ||
        (reply.campaign_name?.toLowerCase().includes(linkedInSearchTerm.toLowerCase()))
      )
    }

    // Client filter - use client_id if available, otherwise use campaign_id mapping as fallback
    // Use effectiveLinkedInClientFilter which combines global and local filters
    if (effectiveLinkedInClientFilter !== 'all') {
      console.log('🔍 LinkedIn Filter: Applying client filter:', {
        effectiveFilter: effectiveLinkedInClientFilter,
        selectedClientName,
        totalRepliesBeforeFilter: filtered.length,
        campaignMappingSize: campaignIdToClientIdMap.size,
        clientNameMappingSize: clientNameToIdMap.size
      })
      
      filtered = filtered.filter(reply => {
        // Primary: Use client_id if it's already mapped (from useLinkedInReplies hook)
        // Handle both number and string types for client_id
        const replyClientId = reply.client_id
        if (replyClientId !== null && replyClientId !== undefined) {
          const replyClientIdStr = typeof replyClientId === 'number' ? replyClientId.toString() : String(replyClientId)
          if (replyClientIdStr === effectiveLinkedInClientFilter) {
            return true
          }
        }
        
        // Fallback 1: If client_id is null, try to resolve from campaign_id mapping
        // This handles cases where the mapping in useLinkedInReplies might have failed
        if (reply.campaign_id) {
          // Try multiple key formats since campaign_id can be string or number
          const campaignId = reply.campaign_id
          const campaignIdStr = String(campaignId)
          const campaignIdNum = typeof campaignId === 'string' && !isNaN(Number(campaignId)) ? Number(campaignId) : (typeof campaignId === 'number' ? campaignId : null)
          
          // Try all possible key formats
          let mappedClientId = campaignIdToClientIdMap.get(campaignId)
          if (!mappedClientId) mappedClientId = campaignIdToClientIdMap.get(campaignIdStr)
          if (!mappedClientId && campaignIdNum !== null) mappedClientId = campaignIdToClientIdMap.get(campaignIdNum)
          
          if (mappedClientId === effectiveLinkedInClientFilter) {
            return true
          }
        }
        
        // Fallback 2: If we have a client name, try matching by campaign name or workspace
        // This is a last resort when ID-based matching fails
        if (selectedClientName) {
          const clientNameLower = selectedClientName.toLowerCase()
          // Check if campaign name contains client name (common pattern)
          if (reply.campaign_name?.toLowerCase().includes(clientNameLower)) {
            return true
          }
          // Check workspace name if available
          if ((reply as any).workspace_name?.toLowerCase() === clientNameLower) {
            return true
          }
        }
        
        // If no match found, exclude from filtered results
        return false
      })
      
      console.log('🔍 LinkedIn Filter: After filtering:', {
        filteredCount: filtered.length,
        effectiveFilter: effectiveLinkedInClientFilter
      })
      
      // TEMPORARY: If filtering results in 0 but we have replies, show all and log detailed debug info
      // This helps us understand why filtering is failing
      if (filtered.length === 0 && linkedInReplies.length > 0) {
        console.warn('⚠️ LinkedIn Filter Warning: Filter resulted in 0 replies but', linkedInReplies.length, 'replies exist')
        console.log('📊 Sample LinkedIn Replies Data:', linkedInReplies.slice(0, 3).map(r => ({
          id: r.id,
          campaign_id: r.campaign_id,
          campaign_name: r.campaign_name,
          client_id: r.client_id,
          client_id_type: typeof r.client_id,
          hasCampaignMapping: r.campaign_id ? campaignIdToClientIdMap.has(r.campaign_id) : false,
          mappedClientId: r.campaign_id ? campaignIdToClientIdMap.get(r.campaign_id) : null
        })))
        console.log('🔑 Filter Details:', {
          effectiveFilter: effectiveLinkedInClientFilter,
          globalFilter: globalClientFilter,
          localFilter: linkedInClientFilter,
          uuidMapping: Array.from(uuidToNumericClientIdMap.entries()).find(([uuid]) => uuid === globalClientFilter),
          campaignMappingSample: Array.from(campaignIdToClientIdMap.entries()).slice(0, 5)
        })
      }
      
      // Debug logging when filter results in 0 replies OR when filter is active
      if (effectiveLinkedInClientFilter !== 'all') {
        const sampleReplies = linkedInReplies.slice(0, 5)
        const repliesWithClientId = linkedInReplies.filter(r => r.client_id !== null && r.client_id !== undefined)
        const repliesWithCampaignId = linkedInReplies.filter(r => r.campaign_id)
        const matchingReplies = linkedInReplies.filter(r => {
          const replyClientId = r.client_id
          if (replyClientId !== null && replyClientId !== undefined) {
            const replyClientIdStr = typeof replyClientId === 'number' ? replyClientId.toString() : String(replyClientId)
            if (replyClientIdStr === effectiveLinkedInClientFilter) return true
          }
          if (r.campaign_id) {
            const mappedClientId = campaignIdToClientIdMap.get(r.campaign_id) || 
                                   campaignIdToClientIdMap.get(String(r.campaign_id)) ||
                                   campaignIdToClientIdMap.get(Number(r.campaign_id))
            if (mappedClientId === effectiveLinkedInClientFilter) return true
          }
          return false
        })
        
        console.log('🔍 LinkedIn Client Filter Debug:', {
          globalClientFilter,
          localClientFilter: linkedInClientFilter,
          effectiveClientFilter: effectiveLinkedInClientFilter,
          totalReplies: linkedInReplies.length,
          filteredCount: filtered.length,
          matchingRepliesCount: matchingReplies.length,
          mappingSize: campaignIdToClientIdMap.size,
          uuidMappingSize: uuidToNumericClientIdMap.size,
          repliesWithClientId: repliesWithClientId.length,
          repliesWithCampaignId: repliesWithCampaignId.length,
          sampleReplies: sampleReplies.map(r => ({
            campaign_id: r.campaign_id,
            campaign_id_type: typeof r.campaign_id,
            campaign_name: r.campaign_name,
            client_id: r.client_id,
            client_id_type: typeof r.client_id,
            mappedClientId: r.campaign_id ? (campaignIdToClientIdMap.get(r.campaign_id) || campaignIdToClientIdMap.get(String(r.campaign_id))) : null,
            matchesFilter: (() => {
              const replyClientId = r.client_id
              if (replyClientId !== null && replyClientId !== undefined) {
                const replyClientIdStr = typeof replyClientId === 'number' ? replyClientId.toString() : String(replyClientId)
                if (replyClientIdStr === effectiveLinkedInClientFilter) return true
              }
              if (r.campaign_id) {
                const mappedClientId = campaignIdToClientIdMap.get(r.campaign_id) || 
                                       campaignIdToClientIdMap.get(String(r.campaign_id))
                if (mappedClientId === effectiveLinkedInClientFilter) return true
              }
              return false
            })()
          })),
          sampleMapping: Array.from(campaignIdToClientIdMap.entries()).slice(0, 5),
          uuidMappingSample: Array.from(uuidToNumericClientIdMap.entries()).slice(0, 5),
          allClientIds: [...new Set(linkedInReplies.map(r => r.client_id).filter(Boolean))]
        })
      }
    }

    // Reply type filter
    if (linkedInReplyTypeFilter !== 'all') {
      filtered = filtered.filter(reply => {
        const replyType = reply.reply_category_type?.toLowerCase() || 'neutral'
        if (linkedInReplyTypeFilter === 'positive') return replyType === 'positive'
        if (linkedInReplyTypeFilter === 'neutral') return replyType === 'neutral'
        if (linkedInReplyTypeFilter === 'negative') return replyType === 'negative'
        if (linkedInReplyTypeFilter === 'ooo') return replyType === 'ooo' || replyType === 'out of office'
        if (linkedInReplyTypeFilter === 'auto-reply') return replyType === 'auto-reply'
        if (linkedInReplyTypeFilter === 'uncategorized') return !reply.reply_category_type && !reply.response_category
        return true
      })
    }

    // Date range filter
    if (linkedInDateRange.from || linkedInDateRange.to) {
      filtered = filtered.filter(reply => {
        const replyDate = new Date(reply.created_at || reply.time_replied || '')
        if (linkedInDateRange.from && replyDate < linkedInDateRange.from) return false
        if (linkedInDateRange.to && replyDate > linkedInDateRange.to) return false
        return true
      })
    }

    return filtered
  }, [linkedInReplies, linkedInSearchTerm, effectiveLinkedInClientFilter, linkedInReplyTypeFilter, linkedInDateRange, campaignIdToClientIdMap])

  // Pagination for LinkedIn Replies
  const linkedInTotalPages = Math.ceil(filteredLinkedInReplies.length / linkedInItemsPerPage)
  const linkedInPaginatedReplies = useMemo(() => {
    const start = (linkedInCurrentPage - 1) * linkedInItemsPerPage
    const end = start + linkedInItemsPerPage
    return filteredLinkedInReplies.slice(start, end)
  }, [filteredLinkedInReplies, linkedInCurrentPage, linkedInItemsPerPage])

  // Clear filters function for LinkedIn
  const clearLinkedInFilters = () => {
    setLinkedInSearchTerm('')
    setLinkedInClientFilter('all')
    setLinkedInReplyTypeFilter('all')
    setLinkedInDateRange({ from: undefined, to: undefined })
    setLinkedInCurrentPage(1)
  }

  // CSV Export function - exports all currently filtered email replies
  const exportToCSV = () => {
    // Use the currently filtered replies for export (regardless of reply type)
    const repliesToExport = filteredReplies

    if (repliesToExport.length === 0) {
      toast({
        title: "No Data to Export",
        description: "No email replies found matching your current filters.",
        variant: "destructive"
      })
      return
    }

    // CSV Headers
    const headers = [
      'Lead Name',
      'Lead Email',
      'Sender Name',
      'Sender Email',
      'Campaign Name',
      'Subject',
      'Reply Text',
      'Category',
      'Reply Type',
      'SDR Status',
      'Response Time',
      'Time Replied',
      'App URL'
    ]

    // Convert data to CSV rows
    const csvRows = [
      headers.join(','),
      ...repliesToExport.map(reply => {
        const hasResponded = reply.average_time_to_respond !== null && reply.average_time_to_respond !== undefined
        const waitingForReply = reply.lead_without_a_reply === true
        const status = reply.sdr_response_tracking_status || 'not_tracked'
        
        let sdrStatus = 'Not Tracked'
        let responseTime = 'N/A'
        
        if (hasResponded) {
          sdrStatus = 'Responded'
          const responseTimeSeconds = typeof reply.average_time_to_respond === 'number' && !isNaN(reply.average_time_to_respond)
            ? reply.average_time_to_respond
            : 0
          const days = Math.floor(responseTimeSeconds / 86400)
          const hours = Math.floor((responseTimeSeconds % 86400) / 3600)
          const minutes = Math.floor((responseTimeSeconds % 3600) / 60)
          
          if (days > 0) {
            responseTime = `${days}d ${hours}h ${minutes}m`
          } else if (hours > 0) {
            responseTime = `${hours}h ${minutes}m`
          } else {
            responseTime = `${minutes}m`
          }
        } else if (waitingForReply || status === 'tracking') {
          sdrStatus = 'Waiting'
        }

        // Escape CSV values (handle commas, quotes, newlines)
        const escapeCSV = (value: any): string => {
          if (value === null || value === undefined) return ''
          const str = String(value)
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`
          }
          return str
        }

        return [
          escapeCSV(reply.to_name || ''),
          escapeCSV(reply.to_email || ''),
          escapeCSV(reply.from_name || ''),
          escapeCSV(reply.from_email || ''),
          escapeCSV(reply.campaign_name || ''),
          escapeCSV(reply.subject || ''),
          escapeCSV(reply.preview_text || reply.reply_body || ''),
          escapeCSV(reply.response_category || ''),
          escapeCSV(reply.reply_category_type || ''),
          escapeCSV(sdrStatus),
          escapeCSV(responseTime),
          escapeCSV(reply.created_at ? formatDate(reply.created_at) : ''),
          escapeCSV(reply.app_url || '')
        ].join(',')
      })
    ]

    // Create CSV blob and download
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', `email-replies-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast({
      title: "CSV Exported",
      description: `Exported ${repliesToExport.length} email reply(ies) to CSV.`,
      variant: "default"
    })
  }

  const handleEditReply = (reply: any) => {
    setEditingReply(reply)
    setEditedFields({
      response_category: reply.response_category || '',
      to_email: reply.to_email || '',
      campaign_name: reply.campaign_name || '',
      subject: reply.subject || '',
      reply_body: reply.reply_body || ''
    })
    setEditDialogOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!editingReply) return;

    try {
      // PHASE 3 DAY 6: Update reply fields in email_events (migrated table)
      const { error } = await supabase
        .from('obsync_dynamic_reports')
        .update({
          response_category: editedFields.response_category,
          to_email: editedFields.to_email,
          campaign_name: editedFields.campaign_name,
          subject: editedFields.subject,
          reply_body: editedFields.reply_body,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingReply.id);

      if (error) {
        console.error('Error updating reply:', error);
        toast({
          title: "Error",
          description: "Failed to update reply",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success", 
          description: "Reply updated successfully"
        });
        setEditDialogOpen(false);
        setEditingReply(null);
        refetch(); // Refresh the data
      }
    } catch (error) {
      console.error('Error updating reply:', error);
      toast({
        title: "Error",
        description: "Failed to update reply",
        variant: "destructive"
      });
    }
  }

  // Removed enrichment functions - no longer needed

  if (error) {
    return (
      <NewAppLayout fullWidth>
        <div className="text-center py-12">
                <div className="text-red-600 space-y-2">
                  <p>Error loading auto replies: {error.message}</p>
                  {error.message?.includes('JSON') || error.message?.includes('Unterminated') ? (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-2">This appears to be a cache corruption issue. Clearing cache...</p>
                      <button
                        onClick={() => {
                          // Clear the corrupted cache
                          try {
                            localStorage.removeItem('auto-replies-cache')
                            console.log('🗑️ Cleared corrupted cache')
                            // Refresh the page to reload data
                            window.location.reload()
                          } catch (e) {
                            console.error('Failed to clear cache:', e)
                          }
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        Clear Cache & Reload
                      </button>
                    </div>
                  ) : null}
                </div>
          <Button onClick={() => { refetchReplies(); refetchMetrics(); }} className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </NewAppLayout>
    )
  }

  return (
    <NewAppLayout fullWidth>
      <div className="h-full flex flex-col space-y-4">
              {/* Page Toolbar Below Standard Header */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold">AI Reply Tagger</h1>
                  <p className="text-muted-foreground mt-1">
                    Monitor and Analyze email & linkedin replies from your campaigns
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <div className="text-sm text-muted-foreground whitespace-nowrap">
                    Total Replies: <span className="font-semibold">{activeTab === 'linkedin' ? linkedInTotalReplies : totalReplies}</span>
                  </div>
                  <Button 
                    onClick={() => { 
                      if (activeTab === 'linkedin') {
                        refetchLinkedInReplies()
                        refetchLinkedInMetrics()
                      } else {
                        refetchReplies()
                        refetchMetrics()
                      }
                    }} 
                    disabled={activeTab === 'linkedin' ? isLoadingLinkedIn : isLoading}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${(activeTab === 'linkedin' ? isLoadingLinkedIn : isLoading) ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <LLMSettingsDialog 
                    settings={llmSettings}
                    onSettingsChange={setLLMSettings}
                    context="ai-reply-tagger"
                  />
                  <Button 
                    onClick={() => setActiveTab('notifications')}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Bell className="w-4 h-4" />
                    Notification Settings
                  </Button>
                </div>
              </div>

            {/* Statistics Cards - Show different metrics based on active tab */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 flex-shrink-0">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Replies</CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activeTab === 'linkedin' ? linkedInTotalReplies : totalReplies}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Today Replies</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activeTab === 'linkedin' ? linkedInTodayReplies : todayReplies}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Month to Date</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activeTab === 'linkedin' ? linkedInMonthToDateReplies : monthToDateReplies}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Positive Replies</CardTitle>
                  <ThumbsUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activeTab === 'linkedin' ? linkedInPositiveReplies : positiveReplies}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Auto Replies</CardTitle>
                  <Bot className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activeTab === 'linkedin' ? linkedInAutoReplies : autoReplies}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'replies' | 'linkedin' | 'notifications' | 'orchestrator')} className="w-full flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                <TabsTrigger value="replies">Email Replies</TabsTrigger>
                <TabsTrigger value="linkedin">LinkedIn Replies</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="orchestrator">Cross-Channel</TabsTrigger>
              </TabsList>
              
              <TabsContent value="replies" className="flex flex-col !mt-2">
                <Card className="flex flex-col w-full">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle>Email Replies</CardTitle>
                    <CardDescription>
                      Latest email replies received from your campaigns
                    </CardDescription>
                    
                     {/* Filters Section */}
                     <div className="space-y-3 pt-4 border-t">
                       {/* First row - Search extending to the right with rows per page */}
                       <div className="flex items-center gap-4">
                         <div className="flex items-center gap-2 flex-1">
                           <Search className="h-4 w-4 text-muted-foreground" />
                           <Input
                             placeholder="Search by email..."
                             value={searchTerm}
                             onChange={(e) => setSearchTerm(e.target.value)}
                             className="flex-1"
                           />
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                           <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
                             <SelectTrigger className="w-20">
                               <SelectValue />
                             </SelectTrigger>
                             <SelectContent>
                                <SelectItem value="15">15</SelectItem>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                                <SelectItem value="500">500</SelectItem>
                                <SelectItem value="1000">1000</SelectItem>
                                <SelectItem value="5000">5000</SelectItem>
                             </SelectContent>
                           </Select>
                         </div>
                       </div>

                       {/* Second row - All other filters right-aligned */}
                       <div className="flex flex-wrap items-center justify-end gap-4">
                         <Select value={clientFilter} onValueChange={setClientFilter}>
                           <SelectTrigger className="w-40">
                             <SelectValue placeholder="All Clients" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All Clients</SelectItem>
                             {uniqueClients.map(client => (
                               <SelectItem key={client.id} value={client.id}>
                                 {client.name}
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>

                        <Select value={categorizationFilter} onValueChange={setCategorizationFilter}>
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="All Categories" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {/* 15 specific categories from audit */}
                            <SelectItem value="Campaign Mess Up">Campaign Mess Up</SelectItem>
                            <SelectItem value="Out Of Office">Out Of Office</SelectItem>
                            <SelectItem value="Auto-Reply">Auto-Reply</SelectItem>
                            <SelectItem value="Wrong Person">Wrong Person</SelectItem>
                            <SelectItem value="No Longer Works Here">No Longer Works Here</SelectItem>
                            <SelectItem value="Do Not Contact">Do Not Contact</SelectItem>
                            <SelectItem value="Interested">Interested</SelectItem>
                            <SelectItem value="Reach Out Later">Reach Out Later</SelectItem>
                            <SelectItem value="Not Interested">Not Interested</SelectItem>
                            <SelectItem value="Specific Questions">Specific Questions</SelectItem>
                            <SelectItem value="Sender Originated Bounce">Sender Originated Bounce</SelectItem>
                            <SelectItem value="Information Request">Information Request</SelectItem>
                            <SelectItem value="Uncategorizable by Ai">Uncategorizable by Ai</SelectItem>
                            <SelectItem value="Meeting Request">Meeting Request</SelectItem>
                            <SelectItem value="Pricing Inquiry">Pricing Inquiry</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select value={replyTypeFilter} onValueChange={setReplyTypeFilter}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Reply Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Reply Types</SelectItem>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="neutral">Neutral</SelectItem>
                            <SelectItem value="negative">Negative</SelectItem>
                            <SelectItem value="ooo">OOO</SelectItem>
                            <SelectItem value="auto-reply">Auto-Reply</SelectItem>
                            <SelectItem value="uncategorized">Uncategorized</SelectItem>
                          </SelectContent>
                        </Select>

                         <Select value={replyCountFilter} onValueChange={setReplyCountFilter}>
                           <SelectTrigger className="w-40">
                             <SelectValue placeholder="Reply Count" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All Counts</SelectItem>
                             <SelectItem value="1">First Replies</SelectItem>
                             <SelectItem value="2">Second Replies</SelectItem>
                             <SelectItem value="later">Later Replies</SelectItem>
                           </SelectContent>
                         </Select>

                         <Select value={sdrStatusFilter} onValueChange={setSdrStatusFilter}>
                           <SelectTrigger className="w-48">
                             <SelectValue placeholder="SDR Status" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All SDR Status</SelectItem>
                             <SelectItem value="responded">Responded</SelectItem>
                             <SelectItem value="waiting">Waiting</SelectItem>
                           </SelectContent>
                         </Select>

                       <DateRangeFilter
                           dateRange={dateRange}
                           onDateRangeChange={setDateRange}
                         />

                       {/* CSV Download Button - always available for quick export */}
                       <Button
                         onClick={exportToCSV}
                         variant="outline"
                         className="gap-2"
                         title="Download current filtered email replies as CSV"
                       >
                         <Download className="h-4 w-4" />
                         Download CSV
                       </Button>

                         {/* TEMPORARY: Bulk ignore button for Cruva one-time fix */}
                         {/* TODO: REMOVE THIS BUTTON AFTER BULK IGNORE IS COMPLETE */}
                         {categorizationFilter === 'auto_replies_to_ignore' && (() => {
                           const CRUVA_CLIENT_ID = 167887
                           const autoReplyCategories = ['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'];
                           const cruvaRepliesToIgnore = filteredReplies.filter(reply => 
                             reply.client_id === CRUVA_CLIENT_ID &&
                             autoReplyCategories.includes(reply.response_category || '') &&
                             reply.reply_count === 1 &&
                             reply.stats_id &&
                             reply.smartlead_ignore_status !== 'success'
                           );
                           
                           if (cruvaRepliesToIgnore.length > 0) {
                             return (
                               <Button
                                 onClick={handleBulkIgnoreCruvaAutoReplies}
                                 disabled={bulkIgnoring}
                                 variant="outline"
                                 className="border-orange-500 text-orange-600 hover:bg-orange-50"
                               >
                                 {bulkIgnoring ? (
                                   <>
                                     <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                     Ignoring {bulkIgnoreProgress.current}/{bulkIgnoreProgress.total}...
                                   </>
                                 ) : (
                                   <>
                                     <XCircle className="w-4 h-4 mr-2" />
                                     Bulk Ignore Cruva ({cruvaRepliesToIgnore.length})
                                   </>
                                 )}
                               </Button>
                             );
                           }
                           return null;
                         })()}
                         {/* END TEMPORARY: Bulk ignore button */}

                         {(searchTerm || clientFilter !== 'all' || categorizationFilter !== 'all' || replyTypeFilter !== 'all' || replyCountFilter !== 'all' || dateRange.from || dateRange.to) && (
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={clearAllFilters}
                             className="flex items-center gap-2"
                           >
                             <X className="h-4 w-4" />
                             Clear Filters
                           </Button>
                         )}
                       </div>

                       {/* TEMPORARY: Bulk ignore progress display */}
                       {/* TODO: REMOVE THIS AFTER BULK IGNORE IS COMPLETE */}
                       {bulkIgnoring && (
                         <Card className="mt-4 border-orange-200 bg-orange-50">
                           <CardContent className="pt-6">
                             <div className="space-y-2">
                               <div className="flex justify-between text-sm">
                                 <span className="font-medium">Processing Cruva auto-replies...</span>
                                 <span>{bulkIgnoreProgress.current} / {bulkIgnoreProgress.total}</span>
                               </div>
                               <div className="w-full bg-gray-200 rounded-full h-2">
                                 <div 
                                   className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                                   style={{ width: `${bulkIgnoreProgress.total > 0 ? (bulkIgnoreProgress.current / bulkIgnoreProgress.total) * 100 : 0}%` }}
                                 />
                               </div>
                               <div className="flex justify-between text-xs text-gray-600">
                                 <span>✅ Succeeded: {bulkIgnoreProgress.succeeded}</span>
                                 <span>❌ Failed: {bulkIgnoreProgress.failed}</span>
                               </div>
                             </div>
                           </CardContent>
                         </Card>
                       )}
                       {/* END TEMPORARY: Bulk ignore progress display */}

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Showing {paginatedReplies.length} of {filteredReplies.length} replies</span>
                        {filteredReplies.length !== replies.length && (
                          <span>({replies.length - filteredReplies.length} filtered out)</span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col p-0">
                    <div className="p-4 flex-1">
                      <RepliesTable
                        replies={filteredReplies}
                        filteredReplies={filteredReplies}
                        totalReplies={filteredReplies.length}
                        onReplyClick={(reply) => {
                          setSelectedReply(reply)
                          setSelectedReplySource('email')
                          fetchFeedbackStatus(reply.id)
                        }}
                        onCategorize={handleCategorize}
                        categorizingIds={categorizingIds}
                        onSendTestNotification={handleSendTestNotification}
                        sendingTestNotificationId={sendingTestNotificationId}
                        isLoading={isLoading && !replies.length}
                        isFetching={isFetching}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setItemsPerPage}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        clientFilter={clientFilter}
                        onClientFilterChange={setClientFilter}
                        categorizationFilter={categorizationFilter}
                        onCategorizationFilterChange={setCategorizationFilter}
                        sdrStatusFilter={sdrStatusFilter}
                        onSdrStatusFilterChange={setSdrStatusFilter}
                        dateRange={dateRange}
                        onDateRangeChange={setDateRange}
                        uniqueClients={uniqueClients}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* LinkedIn Replies Tab */}
              <TabsContent value="linkedin" className="flex flex-col !mt-2">
                <Card className="flex flex-col w-full">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle>LinkedIn Replies</CardTitle>
                    <CardDescription>
                      Latest LinkedIn replies received from your campaigns
                    </CardDescription>
                    
                     {/* Filters Section */}
                     <div className="space-y-3 pt-4 border-t">
                       {/* First row - Search extending to the right with rows per page */}
                       <div className="flex items-center gap-4">
                         <div className="flex items-center gap-2 flex-1">
                           <Search className="h-4 w-4 text-muted-foreground" />
                           <Input
                             placeholder="Search by email or name..."
                             value={linkedInSearchTerm}
                             onChange={(e) => setLinkedInSearchTerm(e.target.value)}
                             className="flex-1"
                           />
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                           <Select value={linkedInItemsPerPage.toString()} onValueChange={(value) => setLinkedInItemsPerPage(Number(value))}>
                             <SelectTrigger className="w-20">
                               <SelectValue />
                             </SelectTrigger>
                             <SelectContent>
                                <SelectItem value="15">15</SelectItem>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                                <SelectItem value="500">500</SelectItem>
                                <SelectItem value="1000">1000</SelectItem>
                                <SelectItem value="5000">5000</SelectItem>
                             </SelectContent>
                           </Select>
                         </div>
                       </div>

                       {/* Second row - All other filters right-aligned */}
                       <div className="flex flex-wrap items-center justify-end gap-4">
                         <Select 
                           value={effectiveLinkedInClientFilter} 
                           onValueChange={(value) => {
                             console.log('🔍 LinkedIn Table Filter Changed:', { from: effectiveLinkedInClientFilter, to: value })
                             setLinkedInClientFilter(value)
                           }}
                         >
                           <SelectTrigger className="w-40">
                             <SelectValue placeholder="All Clients" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All Clients</SelectItem>
                             {uniqueClients.map(client => (
                               <SelectItem key={client.id} value={client.id}>
                                 {client.name} ({client.id})
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>

                        <Select value={linkedInReplyTypeFilter} onValueChange={setLinkedInReplyTypeFilter}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Reply Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Reply Types</SelectItem>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="neutral">Neutral</SelectItem>
                            <SelectItem value="negative">Negative</SelectItem>
                            <SelectItem value="ooo">OOO</SelectItem>
                            <SelectItem value="auto-reply">Auto-Reply</SelectItem>
                            <SelectItem value="uncategorized">Uncategorized</SelectItem>
                          </SelectContent>
                        </Select>

                         <DateRangeFilter
                           dateRange={linkedInDateRange}
                           onDateRangeChange={setLinkedInDateRange}
                         />

                         {(linkedInSearchTerm || effectiveLinkedInClientFilter !== 'all' || linkedInReplyTypeFilter !== 'all' || linkedInDateRange.from || linkedInDateRange.to) && (
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={clearLinkedInFilters}
                             className="flex items-center gap-2"
                           >
                             <X className="h-4 w-4" />
                             Clear Filters
                           </Button>
                         )}
                       </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Showing {linkedInPaginatedReplies.length} of {filteredLinkedInReplies.length} replies</span>
                        {filteredLinkedInReplies.length !== linkedInReplies.length && (
                          <span>({linkedInReplies.length - filteredLinkedInReplies.length} filtered out)</span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col p-0">
                    <div className="p-4 flex-1">
                      <RepliesTable
                        replies={linkedInReplies}
                        filteredReplies={filteredLinkedInReplies}
                        totalReplies={linkedInReplies.length}
                        onReplyClick={(reply) => {
                          setSelectedReply(reply)
                          setSelectedReplySource('linkedin')
                        }}
                        isLoading={isLoadingLinkedIn && !linkedInReplies.length}
                        isFetching={isFetchingLinkedIn}
                        currentPage={linkedInCurrentPage}
                        totalPages={linkedInTotalPages}
                        itemsPerPage={linkedInItemsPerPage}
                        onPageChange={setLinkedInCurrentPage}
                        onItemsPerPageChange={setLinkedInItemsPerPage}
                        searchTerm={linkedInSearchTerm}
                        onSearchChange={setLinkedInSearchTerm}
                        clientFilter={effectiveLinkedInClientFilter}
                        onClientFilterChange={(value) => {
                          // When user changes filter in table, update local filter
                          // This allows override of global filter
                          setLinkedInClientFilter(value)
                        }}
                        dateRange={linkedInDateRange}
                        onDateRangeChange={setLinkedInDateRange}
                        uniqueClients={uniqueClients}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Notification Settings Tab */}
              <TabsContent value="notifications" className="mt-6">
                <NotificationSettingsTab />
              </TabsContent>

              <TabsContent value="orchestrator" className="mt-6">
                <CrossChannelOrchestratorTab />
              </TabsContent>
              
            </Tabs>

          {/* Reply Details Dialogs - Separate for Email and LinkedIn */}
          {selectedReplySource === 'email' && (
            <EmailReplyDetailsDialog
              reply={selectedReply}
              isOpen={selectedReply !== null}
              onClose={() => {
                setSelectedReply(null)
                setSelectedReplySource(null)
              }}
              onRefresh={() => {
                console.log('🔄 Force refreshing data...')
                refetchReplies()
              }}
              onViewInSmartlead={handleViewInSmartlead}
              onManualFetchReplyCount={handleManualFetchReplyCount}
              onCategorize={handleCategorize}
              onManualSmartleadSync={handleManualSmartleadSync}
              onTrackSDRResponse={handleTrackSDRResponse}
              onIgnoreReply={handleIgnoreReply}
              onSendTestNotification={handleSendTestNotification}
              categorizingIds={categorizingIds}
              trackingSDRIds={trackingSDRIds}
              ignoringIds={ignoringIds}
              sendingTestNotificationId={sendingTestNotificationId}
              fetchingReplyCount={fetchingReplyCount}
            />
          )}
          
          {selectedReplySource === 'linkedin' && (
            <LinkedInReplyDetailsDialog
              reply={selectedReply}
              isOpen={selectedReply !== null}
              onClose={() => {
                setSelectedReply(null)
                setSelectedReplySource(null)
              }}
              onRefresh={() => {
                console.log('🔄 Force refreshing data...')
                refetchLinkedInReplies()
              }}
              onCategorize={handleCategorizeLinkedIn}
              categorizingIds={categorizingIds}
            />
          )}

          {/* Edit Reply Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Reply</DialogTitle>
              </DialogHeader>
              {editingReply && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Response Category</div>
                    <Input
                      id="response_category"
                      value={editedFields.response_category}
                      onChange={(e) => setEditedFields({ ...editedFields, response_category: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">To Email</div>
                    <Input
                      id="to_email"
                      value={editedFields.to_email}
                      onChange={(e) => setEditedFields({ ...editedFields, to_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Campaign Name</div>
                    <Input
                      id="campaign_name"
                      value={editedFields.campaign_name}
                      onChange={(e) => setEditedFields({ ...editedFields, campaign_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Subject</div>
                    <Input
                      id="subject"
                      value={editedFields.subject}
                      onChange={(e) => setEditedFields({ ...editedFields, subject: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Reply Body</div>
                    <Textarea
                      id="reply_body"
                      value={editedFields.reply_body}
                      onChange={(e) => setEditedFields({ ...editedFields, reply_body: e.target.value })}
                      rows={10}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>
                      Save Changes
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Webhook Monitor - moved to bottom */}
          <div className="mt-6">
          </div>
      </div>

      {/* Human-in-the-Loop Feedback Modal */}
      <Dialog open={feedbackModalOpen} onOpenChange={setFeedbackModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Incorrect Labeling Feedback</DialogTitle>
            <DialogDescription>
              Help improve the AI categorization by providing the correct category and explaining why the AI's classification was wrong.
            </DialogDescription>
          </DialogHeader>
          
          {selectedReplyForFeedback && (
            <div className="space-y-4">
              {/* Original AI Classification */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">AI's Original Classification:</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Category:</span> {selectedReplyForFeedback.response_category}</div>
                  <div><span className="font-medium">Reasoning:</span> {selectedReplyForFeedback.reasoning}</div>
                  <div><span className="font-medium">Model:</span> {selectedReplyForFeedback.ai_model_used || 'sonar'}</div>
                </div>
              </div>

              {/* Email Content */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Email Content:</h4>
                <div className="text-sm max-h-32 overflow-y-auto">
                  {selectedReplyForFeedback.reply_message_text || selectedReplyForFeedback.content}
                </div>
              </div>

              {/* Correction Form */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="correction-category" className="text-sm font-medium">Correct Category</label>
                  <Select value={correctionCategory} onValueChange={setCorrectionCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the correct category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Interested">Interested</SelectItem>
                      <SelectItem value="Not Interested">Not Interested</SelectItem>
                      <SelectItem value="Requesting More Info">Requesting More Info</SelectItem>
                      <SelectItem value="Out Of Office">Out Of Office</SelectItem>
                      <SelectItem value="Auto-Reply">Auto-Reply</SelectItem>
                      <SelectItem value="Sender Originated Bounce">Sender Originated Bounce</SelectItem>
                      <SelectItem value="Campaign Mess Up">Campaign Mess Up</SelectItem>
                      <SelectItem value="Uncategorizable by AI">Uncategorizable by AI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="correction-reason" className="text-sm font-medium">Why was the AI wrong?</label>
                  <Textarea
                    id="correction-reason"
                    placeholder="Explain why the AI's classification was incorrect and what the correct interpretation should be..."
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitFeedback}
              disabled={!correctionCategory || !correctionReason || submittingCorrection}
            >
              {submittingCorrection ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Operation Progress Dialog */}
      <BulkOperationProgress
        operationId={operationId}
        isOpen={isProgressOpen}
        onClose={closeProgress}
        onComplete={onOperationComplete}
      />
    </NewAppLayout>
  )
}
